import { Group, Stack } from '@mantine/core';
import { UserAvatar } from '@repo/common/components';
import { IconEmoji } from '@repo/common/components/IconEmoji/IconEmoji';
import { Icon, Text, Title } from '@repo/foundations';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import type { IResourceContentProps } from '../../types';
import {
	formatTeamAccessRequestTeamName,
	getTeamAccessRequestStatusDisplay,
	isTeamAccessRequestEvent,
	TEAM_ACCESS_REQUEST_APPROVED_EVENT,
	TEAM_ACCESS_REQUEST_REJECTED_EVENT,
} from '../../utils/teamAccessRequestUtils';

function getRequestedBy(selected: IResourceContentProps['selected']) {
	const displayName =
		(selected.metadata?.requester_name as string | undefined) ??
		selected.sender?.display_name ??
		'Unknown';
	const userId =
		(selected.metadata?.requester_id as string | undefined) ??
		selected.sender?.id ??
		'requester';

	return {
		id: userId,
		display_name: displayName,
		first_name: '',
		last_name: '',
		profile_picture: selected.sender?.profile_picture ?? null,
	};
}

function getApprovedBy(selected: IResourceContentProps['selected']) {
	if (
		selected.event === TEAM_ACCESS_REQUEST_APPROVED_EVENT ||
		selected.event === TEAM_ACCESS_REQUEST_REJECTED_EVENT
	) {
		if (!selected.sender) {
			return null;
		}

		return {
			id: selected.sender.id,
			display_name: selected.sender.display_name,
			first_name: selected.sender.first_name ?? '',
			last_name: selected.sender.last_name ?? '',
			profile_picture: selected.sender.profile_picture ?? null,
		};
	}

	const resolvedByName = selected.metadata?.resolved_by_name as
		| string
		| undefined;
	const resolvedById = selected.metadata?.resolved_by_user_id as
		| string
		| undefined;
	const resolvedByPicture = selected.metadata?.resolved_by_profile_picture as
		| string
		| undefined;

	if (!resolvedByName) {
		return null;
	}

	return {
		id: resolvedById ?? 'approver',
		display_name: resolvedByName,
		first_name: '',
		last_name: '',
		profile_picture: resolvedByPicture ?? null,
	};
}

function getApprovedTime(selected: IResourceContentProps['selected']) {
	const isApproved =
		selected.event === TEAM_ACCESS_REQUEST_APPROVED_EVENT ||
		selected.metadata?.status === 'approved';

	if (!isApproved) {
		return '-';
	}

	const timestamp =
		(selected.metadata?.resolved_at as string | undefined) ??
		(selected.event === TEAM_ACCESS_REQUEST_APPROVED_EVENT
			? selected.created_at
			: selected.updated_at);

	if (!timestamp) {
		return '-';
	}

	return dayjs(timestamp).format('MMMM D, YYYY h:mm A');
}

function DetailRow({
	label,
	children,
}: {
	label: string;
	children: ReactNode;
}) {
	return (
		<Group spacing="xs" noWrap align="baseline">
			<Text w={128} size="sm" py="3xs" color="text/secondary/default">
				{label}
			</Text>
			{children}
		</Group>
	);
}

export function ResourceTeamAccessRequestContent({
	selected,
}: IResourceContentProps) {
	if (!isTeamAccessRequestEvent(selected.event)) {
		return null;
	}

	const rawTeamName =
		(selected.metadata?.team_name as string | undefined) ??
		selected.resource_title ??
		'Team';
	const teamName = formatTeamAccessRequestTeamName(rawTeamName);
	const teamIcon =
		(selected.metadata?.team_icon as string | undefined) ??
		(selected.metadata?.emoji as string | undefined) ??
		(selected.entity?.icon as string | undefined) ??
		'iconName:users';
	const status = getTeamAccessRequestStatusDisplay(
		selected.event,
		selected.metadata?.status as string | undefined
	);
	const requestedBy = getRequestedBy(selected);
	const approvedBy = getApprovedBy(selected);
	const approvedTime = getApprovedTime(selected);

	return (
		<Stack spacing="md">
			<Title size="xl">Access Request for {teamName}</Title>
			<Stack spacing="xs">
				<DetailRow label="Status">
					<Group spacing="2xs" noWrap>
						<Icon
							name={status.iconName}
							color={status.color}
							fillColor={status.fillColor}
						/>
						<Text size="sm">{status.label}</Text>
					</Group>
				</DetailRow>
				<DetailRow label="Team">
					<Group spacing="2xs" noWrap>
						<IconEmoji value={teamIcon} size="md" />
						<Text size="sm">{rawTeamName}</Text>
					</Group>
				</DetailRow>
				<DetailRow label="Requested by">
					<Group spacing="2xs" noWrap>
						<UserAvatar user={requestedBy} size="xs" />
						<Text size="sm">{requestedBy.display_name}</Text>
					</Group>
				</DetailRow>
				<DetailRow label="Approved by">
					{approvedBy ? (
						<Group spacing="2xs" noWrap>
							<UserAvatar user={approvedBy} size="xs" />
							<Text size="sm">{approvedBy.display_name}</Text>
						</Group>
					) : (
						<Text size="sm">-</Text>
					)}
				</DetailRow>
				<DetailRow label="Approved time">
					<Text size="sm">{approvedTime}</Text>
				</DetailRow>
			</Stack>
		</Stack>
	);
}
